import { dialog } from "../../../../nq-component";
import BaseFormPresenter from "../../../../base/BaseFormPresenter";
import DialogModal from "../../../../components/Dialog";
import {
  computePartialCosts,
  computeTotalCost,
} from "../../../../componentCostUtils";
import { da } from "date-fns/locale";
import { findObjectUseCase } from "../../../../usecases/object";
import { auditLogUseCase } from "../../../../usecases/logs";

class DirectPurchaseFormPresenter extends BaseFormPresenter {
  onChange(value, field) {
    this.change[field] = value;
    this.object[field] = value;

    // Preserve raw_materials if present in the view
    if (!this.object.raw_materials && this.view.state.object?.raw_materials) {
      this.object.raw_materials = [...this.view.state.object.raw_materials];
    }

    this.view.setObject(this.object);
  }

  async getObject() {
    const collection = this.view.getCollectionName();
    const id = this.object.id || this.view.getObjectId();
    if (id) {
      const params = { include: ["raw_materials", "raw_materials.uom", "uom"] };
      try {
        this.view.showProgress();
        this.object = await this.getObjectUseCase.execute(collection, id, {
          params,
        });
        this.view.hideProgress();
        this.view.setObject(this.object);
      } catch (error) {
        this.view.hideProgress();
        this.view.showError(error);
      }
    }
  }

  async save() {
    this.view.showProgressDialog();

    try {
      await this._validateAndSave();
      this.change = {};
      this.view.hideProgressDialog();
      dialog.fire({
        html: (
          <DialogModal
            icon="bi bi-question-octagon-fill"
            iconColor="#82c14a"
            title="Saved Successfully"
            message=""
            isCancelButton={false}
            onSubmit={() => {
              this.view.navigateBack();
              dialog.close();
            }}
            onClose={() => dialog.close()}
          />
        ),
        footer: false,
      });
    } catch (error) {
      console.error("Error saving materials:", error);
    }
  }

  async _validateAndSave() {
    const collection = this.view.getCollectionName();
    const user = this.view.getCurrentUser();
    const storeId = user.storeAnalytic?.[0]?.id;

    if (!storeId) {
      throw new Error("Store ID is required");
    }

    // Prepare base data
    if (this.object.id) {
      this.change.id = this.object.id;
    } else {
      this.change.acl = this.view.getAcl();
    }

    const rawMaterials = computePartialCosts(
      this.view.state.object.raw_materials || [],
    );

    if (!rawMaterials.length) {
      throw new Error("No materials to save");
    }

    this._validateMaterials(rawMaterials);

    this.change.raw_materials = rawMaterials;

    // Process each material
    for (const material of rawMaterials) {
      await this._saveMaterial(material, collection, user, storeId);
    }
  }

  _validateMaterials(materials) {
    for (const material of materials) {
      if (!material?.amountDirect) {
        this.view.hideProgressDialog();
        this.view.showError(
          `Amount is required for ${material.itemName || "material"}`,
        );
        throw new Error(
          `Amount is required for ${material.itemName || "material"}`,
        );
      }

      if (!material?.quantity || parseFloat(material.quantity) <= 0) {
        this.view.hideProgressDialog();
        this.view.showError(
          `Valid quantity is required for ${material.itemName || "material"}`,
        );
        throw new Error(
          `Valid quantity is required for ${material.itemName || "material"}`,
        );
      }

      if (!material?.itemName) {
        this.view.hideProgressDialog();
        this.view.showError("Item name is required");
        throw new Error("Item name is required");
      }
    }
  }

  async _saveMaterial(material, collection, user, storeId) {
    const payload = this._buildMaterialPayload(material, user, storeId);

    const directPurchaseResponse = await this.upsertUseCase.execute(
      collection,
      payload,
    );

    const inventoryResponse = await this._updateInventory(
      material,
      user,
      storeId,
    );

    await this._saveDirectTransaction(
      material,
      directPurchaseResponse,
      storeId,
    );

    await this._logMaterialSave(
      material,
      collection,
      directPurchaseResponse,
      inventoryResponse,
      user,
      storeId,
    );
  }

  _buildMaterialPayload(material, user, storeId) {
    delete material?.category;
    const {
      id,
      component_quantity,
      acl,
      created,
      updated,
      stock_quantity,
      unit_cost,
      partial_cost,
      cost,
      ...cleanedMaterial
    } = material;

    return {
      ...cleanedMaterial,
      description: this.change.description,
      quantity: String(material.quantity),
      uom: { id: material.uom.id },
      price: material.costDirect,
      sku: material.sku,
      itemName: material.itemName,
      raw_materials: this.change.raw_materials,
      storeId: { id: storeId },
    };
  }

  async _updateInventory(material, user, storeId) {
    const inventoryItems = await findObjectUseCase().execute("inventory", {
      where: {
        itemName: material.itemName,
        "storeId.id": { $eq: storeId },
      },
      limit: 1,
    });

    if (!inventoryItems?.length) {
      return null;
    }

    const currentInventory = inventoryItems[0];
    const currentCount = parseFloat(currentInventory.systemCount || "0");
    const addedQty = parseFloat(material.quantity || "0");
    const updatedCount = (currentCount + addedQty).toFixed(2);

    const inventoryResponse = await this.upsertUseCase.execute("inventory", {
      id: currentInventory.id,
      systemCount: String(updatedCount),
    });

    await auditLogUseCase().create({
      user: { id: user.id },
      logDate: new Date().toISOString(),
      message: `Added ${addedQty} to inventory: ${material.itemName}, old stock: ${currentCount}, new stock: ${updatedCount}`,
      collection: "inventory",
      objectId: currentInventory.id,
      payload: { material, inventoryResponse },
      store: { id: storeId },
    });

    return inventoryResponse;
  }

  async _saveDirectTransaction(material, directPurchaseResponse, storeId) {
    try {
      const payload = {
        storeId: { id: storeId },
        sku: material.sku,
        item_name: material.itemName,
        transaction_type: "Direct Purchase",
        quantity: String(material.quantity),
        business_date: new Date().toISOString(),
        reference_id: directPurchaseResponse?.id,
      };

      await this.upsertUseCase.execute("inventory_transaction", payload);
    } catch (error) {
      throw error;
    }
  }

  async _logMaterialSave(
    material,
    collection,
    directPurchaseResponse,
    inventoryResponse,
    user,
    storeId,
  ) {
    await auditLogUseCase().create({
      user: { id: user.id },
      logDate: new Date().toISOString(),
      message: `Saved direct purchase for ${material.itemName}, quantity: ${material.quantity}, total cost: ${material.cost}`,
      collection,
      objectId: directPurchaseResponse.id,
      payload: {
        material,
        directPurchaseResponse,
        inventoryResponse,
      },
      store: { id: storeId },
    });
  }

  async submit() {
    // if (Object.values(this.change).length === 0) {
    //   this.view.showSuccessSnackbar("Successfully saved!");
    //   return;
    // }
    try {
      this.view.submitting();
      dialog.close();
      await this.save();
    } catch (error) {
      this.view.submissionError(error);
      this.view.showError(error);
    }
  }
}

export default DirectPurchaseFormPresenter;
